import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronLeft, ChevronRight, Filter, 
  Droplets, Flame, Wind, Wrench, Phone,
ArrowRight} from 'lucide-react';
import { Helmet } from 'react-helmet';
import { Facebook, Instagram } from 'lucide-react';
const GalleryPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);

  const categories = [
    { id: 'all', name: 'All Projects', icon: Wrench },
    { id: 'plumbing', name: 'Plumbing', icon: Droplets },
    { id: 'heating', name: 'Heating', icon: Flame },
    { id: 'cooling', name: 'Air Conditioning', icon: Wind }
  ];

  const projects = [
    {
      id: 1,
      title: 'Emergency Plumbing Repair',
      category: 'plumbing',
      image: '/images/Gallery/gallery_img_6.webp',
      description: 'Quick response to a burst pipe emergency in Budd Lake. Complete repair with minimal disruption.',
      location: 'Budd Lake, NJ',
      beforeAfter: false
    },
    {
      id: 2,
      title: 'HVAC System Installation',
      category: 'cooling',
      image: 'images/Gallery/gallery_img_4.webp',
      description: 'Complete HVAC system installation for a new home in Mount Olive.',
      location: 'Mount Olive, NJ',
      beforeAfter: false
    },
    {
      id: 3,
      title: 'Water Heater Replacement',
      category: 'plumbing',
      image: 'images/Gallery/gallery_img_1.webp',
      description: 'Tankless water heater installation with energy efficiency upgrades.',
      location: 'Hackettstown, NJ',
      beforeAfter: false
    },
    {
      id: 4,
      title: 'Professional Plumbing Service',
      category: 'plumbing',
      image: 'images/Gallery/gallery_img_5.webp',
      description: 'Professional plumbing maintenance and repair service.',
      location: 'Chester, NJ',
      beforeAfter: false
    },
    {
      id: 5,
      title: 'Heating System Maintenance',
      category: 'heating',
      image: 'images/Gallery/gallery_img_2.webp',
      description: 'Annual heating system maintenance and tune-up service.',
      location: 'Randolph, NJ',
      beforeAfter: false
    },
    {
      id: 6,
      title: 'Professional Tools & Equipment',
      category: 'plumbing',
      image: 'images/Gallery/gallery_img_3.webp',
      description: 'State-of-the-art professional tools and equipment for quality service.',
      location: 'Our Workshop',
      beforeAfter: false
    }
  ];

  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(project => project.category === selectedCategory);

  const nextImage = () => {
    if (selectedImage) {
      const currentIndex = filteredProjects.findIndex(p => p.id === selectedImage.id);
      const nextIndex = (currentIndex + 1) % filteredProjects.length;
      setSelectedImage(filteredProjects[nextIndex]);
    }
  };

  const prevImage = () => {
    if (selectedImage) {
      const currentIndex = filteredProjects.findIndex(p => p.id === selectedImage.id);
      const prevIndex = currentIndex === 0 ? filteredProjects.length - 1 : currentIndex - 1;
      setSelectedImage(filteredProjects[prevIndex]);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
     <section className="relative py-20 text-white overflow-hidden">
  {/* Background Image - adjusted for better visibility */}
  <div
    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
    style={{ 
      backgroundImage: "url('/images/bg-about_hero.webp')",
      filter: "brightness(1.2)"  // Brighten the image
    }}
  ></div>
  
  {/* Lighter overlay with gradient for better image visibility */}
  <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
  
  <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <motion.div
      className="text-center space-y-8"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      <h1 className="text-5xl lg:text-6xl font-bold leading-tight drop-shadow-lg">
        Our Work
        <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent block">
          Gallery
        </span>
      </h1>
      <p className="text-xl text-white/95 max-w-3xl mx-auto leading-relaxed drop-shadow-md">
        Take a look at some of our recent projects and see the quality workmanship that has made us Morris County's trusted choice for plumbing, heating, and air conditioning services.
      </p>
    </motion.div>
  </div>
</section>

      {/* Category Filter */}
      <section className="py-12 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="flex flex-wrap justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {categories.map((category) => (
              <motion.button
                key={category.id}
                className={`flex items-center space-x-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-200'
                }`}
                onClick={() => setSelectedCategory(category.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <category.icon className="h-5 w-5" />
                <span>{category.name}</span>
              </motion.button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            layout
          >
            <AnimatePresence>
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden cursor-pointer group"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                  onClick={() => setSelectedImage(project)}
                  layout
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    {project.beforeAfter && (
                      <div className="absolute top-4 left-4">
                        <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          Before & After
                        </div>
                      </div>
                    )}

                    <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <h3 className="text-lg font-bold mb-1">{project.title}</h3>
                      <p className="text-blue-200 text-sm">{project.location}</p>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{project.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-blue-600 font-medium">{project.location}</span>
                      <div className="flex items-center text-gray-400">
                        <Filter className="h-4 w-4 mr-1" />
                        <span className="text-sm capitalize">{project.category}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {filteredProjects.length === 0 && (
            <motion.div
              className="text-center py-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="text-gray-400 text-lg">No projects found in this category.</div>
            </motion.div>
          )}
        </div>
      </section>
      {/* Add this after your existing gallery section */}

 <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="bg-gray-50 rounded-3xl p-10 md:p-12"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col-reverse lg:flex-row gap-8 items-center">
            {/* Left Column - Text and Social Icons */}
            <div className="flex-1 lg:pr-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Explore More Our Completed Projects
              </h2>
              <p className="text-gray-600 text-lg mb-8">
                From repairs to full installations, our work speaks for itself. Want to see more?
                Follow us on social media to view our history of projects and stay updated
                with the latest.
              </p>
              {/* Social Icons */}
              <div className="flex gap-4">
                <motion.a
                  href="https://www.facebook.com/p/Mount-Olive-Plumbing-100083106291508/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="bg-blue-600 p-3 rounded-xl hover:bg-blue-700 transition-colors border-2 border-[#D0D0D0]">
                    <Facebook className="h-6 w-6 text-white fill-white" />
                  </div>
                </motion.a>
                <motion.a
                  href="https://www.instagram.com/moplumbing2018/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 p-3 rounded-xl hover:from-purple-700 hover:via-pink-600 hover:to-orange-500 transition-all border-2 border-[#D0D0D0]">
                    <Instagram className="h-6 w-6 text-white" />
                  </div>
                </motion.a>
              </div>
            </div>
            
            {/* Right Column - Images */}
            <div className="flex gap-4 justify-center lg:justify-end">
              <motion.div
                className="relative h-48 w-32 md:h-56 md:w-40 overflow-hidden shadow-md"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="/images/Gallery/gallery-extra-1.webp"
                  alt="Plumbing Project 1"
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <motion.div
                className="relative h-48 w-32 md:h-56 md:w-40 overflow-hidden shadow-md"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="/images/Gallery/gallery-extra-2.webp"
                  alt="Plumbing Project 2"
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <motion.div
                className="relative h-48 w-32 md:h-56 md:w-40 overflow-hidden shadow-md"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="/images/Gallery/gallery-extra-3.webp"
                  alt="Plumbing Project 3"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              className="relative max-w-4xl w-full bg-white rounded-3xl overflow-hidden"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                className="absolute top-4 right-4 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors duration-300"
                onClick={() => setSelectedImage(null)}
              >
                <X className="h-6 w-6" />
              </button>

              {/* Navigation Buttons */}
              <button
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors duration-300"
                onClick={prevImage}
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              
              <button
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors duration-300"
                onClick={nextImage}
              >
                <ChevronRight className="h-6 w-6" />
              </button>

              {/* Image */}
              <div className="relative h-96 lg:h-[500px]">
                <img 
                  src={selectedImage.image} 
                  alt={selectedImage.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content */}
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-3xl font-bold text-gray-900">{selectedImage.title}</h2>
                  {selectedImage.beforeAfter && (
                    <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                      Before & After
                    </div>
                  )}
                </div>
                
                <p className="text-gray-600 text-lg leading-relaxed mb-6">
                  {selectedImage.description}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="text-blue-600 font-semibold">{selectedImage.location}</div>
                    <div className="text-gray-400 capitalize">{selectedImage.category}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

       {/* CTA Section */}
            <section className="relative py-20 text-white overflow-hidden">
              <Helmet>
  <title>Plumbing & HVAC Project Gallery | Mount Olive Plumbing</title>
  <meta name="description" content="View before-and-after photos of plumbing, heating & AC projects completed in Mount Olive, Budd Lake, and surrounding NJ towns." />
  
  {/* Open Graph Tags */}
  <meta property="og:title" content="Plumbing & HVAC Work Gallery – Mount Olive Plumbing" />
  <meta property="og:description" content="See real project photos from homes and businesses in Morris County." />
  <meta property="og:url" content="https://www.mtoliveplumbing.com/gallery" />
  <meta property="og:type" content="website" />
  
  {/* Additional SEO Meta Tags */}
  <meta name="keywords" content="plumbing gallery, HVAC projects, Mount Olive plumber photos, Morris County plumbing work, heating installation photos, AC repair gallery" />
  <link rel="canonical" href="https://www.mtoliveplumbing.com/gallery" />
</Helmet>
              {/* Background Image with Gradient Overlay */}
              <div className="absolute inset-0 z-0">
                <img 
                  src="/images/HomePage/CTAHomePage.webp"
                  alt="Construction and Roadwork Scene"
                  className="w-full h-full object-cover object-top opacity-12"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#2462E9]/90 to-[#1E40AF]/90"></div>
              </div>
      
              <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <motion.div
                  className="space-y-8"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                                             {/* First Text Container */}
                    <div className="mb-6">
                      <h3 className="text-[15px] font-bold text-white mb-4 bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-full px-4 py-2 inline-block">
                        Plumbing & HVAC estimate in Morris County
                      </h3>
                    </div>
      
                   {/* Second Text Container */}
                   <div className="mb-6">
                     <h2 className="text-[70px] font-bold text-white mb-4">
                       Get Your Free <span className="text-[#45C1FF]">Plumbing & HVAC</span> Estimate Today
                     </h2>
                   </div>
      
                   {/* Third Text Container */}
                   <div className="mb-6">
                     <p className="text-[24px] font-bold text-white">
                       Need immediate help or planning a new installation?
                     </p>
                   </div>
      
                   {/* Fourth Text Container */}
                   <div className="mb-8">
                     <p className="text-[20px] font-normal text-white max-w-4xl mx-auto leading-relaxed">
                       Call Mount Olive Plumbing now or request your free, no-obligation estimate. Our licensed team provides expert plumbing, heating, and air conditioning solutions day or night.
                     </p>
                   </div>
                  
                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-6 justify-center">
                                   <motion.a
                       href="tel:+1-973-862-0606"
                       className="inline-flex items-center space-x-3 bg-white text-blue-700 px-8 py-4 rounded-full font-bold text-lg shadow-2xl hover:shadow-white/25 transition-all duration-300"
                       whileHover={{ scale: 1.05 }}
                       whileTap={{ scale: 0.95 }}
                     >
                       <Phone className="h-6 w-6" />
                       <span>Call: (973) 862-0606</span>
                     </motion.a>
                    
                    {/* <motion.button
                      className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full font-semibold border-2 border-white/20 hover:bg-white/20 transition-all duration-300"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span>Get Free Estimate</span>
                      <ArrowRight className="h-5 w-5" />
                    </motion.button> */}
                  </div>
                </motion.div>
              </div>
            </section>
    </div>
  );
};

export default GalleryPage;